/**
 * Area Reinforcement Tool
 * 
 * Creates a grid of rebar over a surface (floor/slab/wall).
 */

import { v4 as uuidv4 } from 'uuid';
import { AreaReinforcementElement, AreaReinforcementGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class AreaReinforcementTool extends BaseTool {
    id = 'area_reinforcement';
    name = 'Area Reinforcement';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;
    private hostId: string | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        // First click - find host (manual scene raycast: element meshes carry
        // no R3F handlers, so event.intersections never resolves BIM elements)
        if (!this.hostId) {
            const hit = this.raycastSceneAtEvent(event).find(i => {
                const id = this.findId(i.object);
                if (!id) return false;
                const el = this.context!.bimStore.project.elements.get(id);
                return el && el.rebarHostData && el.rebarHostData.validHost;
            });

            if (hit) {
                this.hostId = this.findId(hit.object)!;
                const point = this.getIntersection(event);
                if (point) {
                    this.points.push(this.snapToGrid(point));
                }
            }
            return;
        }

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        // Check if closed
        if (this.points.length > 2) {
            const start = this.points[0];
            if (snapped.distanceTo(start) < 0.2) {
                this.points.pop();
                this.finishSketch();
                return;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.hostId = null;
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: previewPoints,
                closed: false,
                color: '#ff00ff'
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context || !this.hostId) return;

        const boundary: [number, number][] = this.points.map(p => [p.x, p.y]);
        const elevation = this.points[0].z;

        const geometryData: AreaReinforcementGeometryData = {
            boundary,
            elevation,
            hostId: this.hostId,
            layers: {
                topMajor: { active: true, barType: '12M', spacing: 200 },
                topMinor: { active: true, barType: '12M', spacing: 200 },
                bottomMajor: { active: true, barType: '12M', spacing: 200 },
                bottomMinor: { active: true, barType: '12M', spacing: 200 }
            }
        };

        const areaReinforcement: AreaReinforcementElement = {
            id: uuidv4(),
            category: 'Structural Area Reinforcement',
            type: 'Area Reinforcement',
            parameters: {
                'Major Direction': 'X',
                'Major Spacing': '200 mm',
                'Major Bar Type': '12M',
                'Minor Direction': 'Y',
                'Minor Spacing': '200 mm',
                'Minor Bar Type': '12M',
                'Top Cover': '25 mm',
                'Bottom Cover': '25 mm'
            },
            constraints: [
                { type: 'HOST_CONTAINED', targetId: this.hostId, data: {} }
            ],
            geometryData
        };

        this.context.bimStore.addElement(areaReinforcement);

        this.points = [];
        this.hostId = null;
        this.context.appStore.setToolPreview(null);
    }
}
